import type { CSSProperties } from "react";
import type { ImageHotspotData } from "@/types/storyboard";
import { CourseMarkdown } from "@/components/storyboard/CourseMarkdown";
import { cn } from "@/lib/utils";

interface HotspotCardProps {
  hotspot: ImageHotspotData;
  x: number;
  y: number;
  /** Referenced by the open pin's `aria-controls`. */
  id: string;
}

// The card is w-56 (14rem) on phones. Half of it fits beside a pin as long as
// the pin is at least that far from the edge - on a 320px stage that is 35% of
// the width,
// and on anything wider the margin only grows. So a pin inside these bands gets
// the card aligned to it rather than centred over it, which keeps the card
// inside the stage at every width instead of hanging off the side.
const EDGE_BAND = 0.35;
// Pin radius plus the gap the card used to get from mb-2.
const PIN_OFFSET = "1.5rem";

// Rounded: 1 - 0.95 lands on 5.000000000000004, and that reaches the DOM.
const percent = (fraction: number) => `${Number((fraction * 100).toFixed(4))}%`;

const horizontalPosition = (x: number): CSSProperties => {
  if (x < EDGE_BAND) return { left: percent(x) };
  if (x > 1 - EDGE_BAND) return { right: percent(1 - x) };
  return { left: percent(x), transform: "translateX(-50%)" };
};

// The card goes on whichever side of the pin has more room. Height follows the
// text, so a long hotspot can extend past the image instead of scrolling.
const verticalPosition = (y: number): CSSProperties =>
  y < 0.5
    ? { top: `calc(${percent(y)} + ${PIN_OFFSET})` }
    : { bottom: `calc(${percent(1 - y)} + ${PIN_OFFSET})` };

export const cardPosition = (x: number, y: number): CSSProperties => ({
  ...horizontalPosition(x),
  ...verticalPosition(y),
});

/**
 * The open hotspot's info, positioned against the stage rather than against its
 * pin: a pin is 32px wide, so percentages inside it say nothing about how much
 * room is left to the edge of the image.
 */
const HotspotCard = ({ hotspot, x, y, id }: HotspotCardProps) => (
  <div
    id={id}
    data-testid="hotspot-card"
    role="note"
    style={cardPosition(x, y)}
    className={cn(
      "absolute z-20 w-56 max-w-full rounded-xl bg-background p-3 sm:w-72",
      // The violet edge is the same colour the open pin turns, so the card
      // reads as belonging to it; the hairline ring and the deeper shadow lift
      // it off busy photography, where a plain border disappears.
      "border-t-2 border-l-2 border-r-2 border-b-2 ring-1 ring-black/10",
      "text-sm shadow-xl"
    )}
    onPointerDown={(e) => e.stopPropagation()}
  >
    <p className="mt-0 font-semibold text-primary">{hotspot.title}</p>
    <div className="prose prose-sm max-w-full prose-p:my-0 text-foreground/80">
      <CourseMarkdown>{hotspot.text}</CourseMarkdown>
    </div>
  </div>
);

export default HotspotCard;
